import { AuthProvider, PreviousLocationStorageKey } from 'react-admin';
import { User, UserManager } from 'oidc-client-ts';
import { performLocalLogout } from './oidc';
import { resolveInstanceAppUrl } from '../helpers/appReturnUrl';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Per-tab sentinel for the one-shot stale-state recovery in handleCallback.
// Survives the re-auth bounce (sessionStorage), cleared on a successful
// callback so a genuinely broken state can't loop forever.
const STALE_STATE_RETRY_KEY = 'orgadmin.staleStateRetry';

// Shared guard: when the app bootstrap initiates its own signinRedirect (the
// cross-app `?sub=` re-auth in AdminDashboard), React-Admin's checkAuth must
// NOT fire a second, competing signinRedirect for the same page load. Two
// concurrent authorize requests race on the shared `oidc.*` localStorage
// state, and the `state` the IdP echoes back can land without a matching
// stored entry — surfacing as "No matching state found in storage" /
// auth.session_expired. Module scope (not storage) so it resets naturally on
// the post-bounce page reload.
let appManagedRedirectInFlight = false;

/**
 * Signal that the app is about to drive its own signinRedirect, so the auth
 * provider's checkAuth stands down instead of launching a competing one.
 */
export const markAppManagedRedirect = (): void => {
  appManagedRedirectInFlight = true;
};

/**
 * Release the guard. Call this if the app-managed signinRedirect fails to
 * launch, so checkAuth isn't left silently suppressed for the rest of the
 * page load.
 */
export const clearAppManagedRedirect = (): void => {
  appManagedRedirectInFlight = false;
};

/**
 * Decode a JWT payload (no signature verification — the token was already
 * obtained over TLS from the OIDC provider). Returns {} on any malformed input.
 *
 * Needed because Sirk's auth server carries the per-org `https://sirk.app/permissions`
 * claim in the ACCESS token, not the ID token. With an empty granted scope it
 * issues no ID token at all, so `user.profile` (built from the ID token / userinfo)
 * has no permissions — the gate must read them from the access token.
 */
function decodeJwtPayload(token: string | undefined): Record<string, unknown> {
  if (!token) return {};
  try {
    const payload = token.split('.')[1];
    if (!payload) return {};
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/**
 * Set the active organization segment from outside React. The auth provider's
 * handleCallback runs before any component mounts, so it can't use a hook to
 * dispatch. A dynamic import avoids a static import cycle
 * (store → organizationsSlice → config/oidc → oidcAuthProvider); the
 * localStorageMiddleware persists the segment as a side effect of the dispatch.
 */
async function selectOrganization(orgId: string): Promise<void> {
  try {
    const [{ store }, { setDynamicSegment }] = await Promise.all([
      import('../store'),
      import('../store/slices/organizationsSlice'),
    ]);
    store.dispatch(setDynamicSegment(orgId));
  } catch (e) {
    console.warn('Failed to set invited organization segment:', e);
  }
}

async function exchangeCodeManually(code: string, redirectUri: string, manager: UserManager): Promise<void> {
  const metadata = await manager.metadataService.getMetadata();
  const res = await fetch(metadata.token_endpoint!, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      client_id: manager.settings.client_id,
      redirect_uri: redirectUri,
    }),
  });
  if (!res.ok) throw new Error(`Token exchange failed: ${res.status}`);
  const tokens = await res.json();
  await manager.storeUser(new User(tokens));
}

/**
 * Creates an authProvider for React Admin that works with any OAuth2/OIDC provider
 *
 * @param userManager The OIDC UserManager instance
 * @param options Configuration options
 * @returns An authProvider ready to be used by React Admin
 */
export const createOidcAuthProvider = (
  userManager: UserManager,
  options: {
    loginRedirectUri?: string;
    logoutRedirectUri?: string;
    redirectOnCheckAuth?: boolean;
  } = {}
): AuthProvider => {
  const {
    loginRedirectUri,
    logoutRedirectUri,
    redirectOnCheckAuth = true,
  } = options;

  return {
    // Called when user clicks login
    async login() {
      await userManager.signinRedirect({
        redirect_uri: loginRedirectUri || `${window.location.origin}/auth-callback`,
      });
    },

    // Called when user clicks logout
    async logout() {
      try {
        await userManager.signoutRedirect({
          post_logout_redirect_uri: logoutRedirectUri || window.location.origin,
          // The auth server needs the client to validate the post-logout redirect, to keep the
          // product's branding on whatever page the user lands on, and to know where to send
          // them if they log in again.
          //
          // Supplied only when the stored user HAS an id_token. oidc-client-ts sets
          // id_token_hint from the stored user and then adds client_id itself precisely when
          // there is none (`!client_id && post_logout_redirect_uri && !id_token_hint`), and
          // SignoutRequest appends rather than replaces — so sending it unconditionally emitted
          // `client_id` twice for magic-link and invitation sessions, which hold no id_token.
          // A strict IdP rejects a repeated parameter as invalid_request (RFC 6749 §3.1).
          // Either way exactly one client_id reaches the end-session endpoint.
          ...((await userManager.getUser())?.id_token
              ? { extraQueryParams: { client_id: userManager.settings.client_id } }
              : {}),
        });
      } catch (error) {
        // Check if error is specifically about missing end_session_endpoint
        const errorMessage = error instanceof Error ? error.message : String(error);
        const isMissingEndpoint = errorMessage.toLowerCase().includes('end_session_endpoint') ||
                                   errorMessage.toLowerCase().includes('no end session endpoint');

        if (isMissingEndpoint) {
          // Expected: OIDC provider doesn't support end_session_endpoint
          console.warn('[OIDC] Provider does not support end_session_endpoint, performing local logout');
          await performLocalLogout(userManager);
          // Redirect to home page
          window.location.href = logoutRedirectUri || window.location.origin;
        } else {
          // Unexpected error (network, etc.) - re-throw to surface the issue
          console.error('[OIDC] Unexpected logout error:', error);
          throw error;
        }
      }
      return false;
    },

    // Called when API returns error
    async checkError({ status }) {
      // Only 401 (unauthenticated) should force re-login. A 403 means the
      // user is authenticated but lacks permission for this action —
      // re-authenticating won't help, so keep the session and let the UI
      // surface the error instead of logging the user out.
      if (status === 401) {
        throw new Error('Unauthorized');
      }
    },

    // Called when user navigates to check authentication
    async checkAuth() {
      // Any code= in the URL means a magic link or OIDC callback is in progress
      const query = window.location.search;
      if (query.includes('code=')) {
        return;
      }

      const user = await userManager.getUser();
      if (!user || user.expired) {
        // The bootstrap may already own a signinRedirect for this page load
        // (e.g. the cross-app `?sub=` re-auth). Don't launch a second,
        // competing authorize request — it would race on the shared OIDC
        // state and orphan the `state` the IdP echoes back. The in-flight
        // redirect will navigate away momentarily. Only suppress when this
        // provider would itself redirect; `redirectOnCheckAuth: false` callers
        // expect the throw below, not a silent no-op.
        if (appManagedRedirectInFlight && redirectOnCheckAuth) {
          return;
        }
        if (redirectOnCheckAuth) {
          localStorage.setItem(
            PreviousLocationStorageKey,
            window.location.href
          );
          await userManager.signinRedirect({
            redirect_uri: loginRedirectUri || `${window.location.origin}/auth-callback`,
          });
          return;
        }
        throw new Error('Not authenticated');
      }
    },

    // Get user permissions/roles.
    // Sirk JWTs carry per-org permissions as `<orgSegment>:<verb>:<resource>`
    // under the `https://sirk.app/permissions` claim. Returned verbatim;
    // callers filter by the active org segment.
    async getPermissions() {
      const user = await userManager.getUser();
      if (!user || user.expired) {
        return;
      }

      // Sirk carries per-org permissions in the ACCESS token, not the ID
      // token. Prefer the access-token claim; fall back to profile (ID
      // token / userinfo) for providers that surface it there instead.
      const accessClaims = decodeJwtPayload(user.access_token);
      const profile = user.profile;
      return (
        accessClaims['https://sirk.app/permissions'] ||
        profile?.['https://sirk.app/permissions'] ||
        profile?.permissions ||
        profile?.roles ||
        profile?.role ||
        profile?.['https://claims/roles'] ||
        profile?.['https://claims/role'] ||
        []
      );
    },

    // Get user identity
    async getIdentity() {
      const user = await userManager.getUser();
      if (!user || user.expired) {
        throw new Error('Not authenticated');
      }

      const profile = user.profile;
      return {
        id: profile?.sub || profile?.email || 'unknown',
        fullName: profile?.name || profile?.email || 'Unknown User',
        avatar: profile?.picture,
      };
    },

    // Handle OAuth callback
    async handleCallback() {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');

      if (!code) {
        throw { message: 'No authorization code in callback.' };
      }

      const callbackUri = loginRedirectUri || `${window.location.origin}/auth-callback`;

      // Invitation magic link: state is an invitationId UUID
      if (state && UUID_REGEX.test(state)) {
        await exchangeCodeManually(code, callbackUri, userManager);

        const user = await userManager.getUser();
        const token = user?.access_token;
        const apiBase = import.meta.env.VITE_API_ORGADMIN_API_BASE;

        // Resolve which org this invitation is for, so the invitee lands in the
        // tenant they were invited to. Without this, org selection falls back to
        // the first org in the user's list (organizationsSlice.fulfilled), so a
        // user who already belongs to other tenants is dropped into the wrong one.
        // The invitation UUID in `state` is the only org-identifying value the
        // auth-backend round-trips to the SPA, hence the lookup.
        let invitedOrgId: string | undefined;
        try {
          const res = await fetch(`${apiBase}/invitation/${state}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const invitation = await res.json();
            invitedOrgId = invitation?.organizationId;
          }
        } catch (e) {
          console.warn('Failed to resolve invited organization:', e);
        }

        try {
          await fetch(`${apiBase}/invitation/${state}/accept`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          });
        } catch (e) {
          console.warn('Invitation accept failed (may already be accepted):', e);
        }

        // Select the invited org before signinSilent triggers the post-login
        // fetchOrganizations, so its reducer keeps this segment instead of
        // defaulting to the first org in the list. This is the fallback for when
        // there is no asset app to forward to (below) — the invitee then stays
        // in orgadmin but still lands in the right tenant.
        if (invitedOrgId) {
          await selectOrganization(invitedOrgId);
        }

        // Refresh the token so the new membership is reflected in this session.
        await userManager.signinSilent();

        // Invited users belong in the calling application, not the admin app —
        // forward them to this instance's app (VITE_DEFAULT_APP_URL, validated
        // against the same allowlist as the "back to app" button). Carry the org
        // as `?org=` so that app can land the invitee in the freshly-joined
        // tenant. Only the instance URL counts here, never the app profile's
        // public site: when no app is configured we fall through and the invitee
        // stays in orgadmin (see resolveInstanceAppUrl).
        const callingAppUrl = resolveInstanceAppUrl();
        if (callingAppUrl) {
          const target = new URL(callingAppUrl);
          if (invitedOrgId) {
            target.searchParams.set('org', invitedOrgId);
          }
          window.location.assign(target.toString());
        }
        return;
      }

      // LOGIN magic link: code with no state — manual exchange
      if (!state) {
        await exchangeCodeManually(code, callbackUri, userManager);
        return;
      }

      // Standard OIDC authorization code flow (state is OIDC state, not UUID)
      try {
        await userManager.signinRedirectCallback();
        // Clean callback — clear any pending stale-state recovery sentinel.
        sessionStorage.removeItem(STALE_STATE_RETRY_KEY);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Login callback failed';
        // oidc-client-ts throws the untyped internal Error "No matching state
        // found in storage" when the `state` echoed by the IdP has no
        // counterpart in this origin's localStorage — a stale/cleared callback
        // URL, or a redirect whose state was clobbered by a competing authorize
        // request. The shared IdP session means a single clean re-auth recovers
        // transparently, so bounce once instead of dead-ending on an
        // "expired session" screen. The per-tab sentinel makes it one-shot: if
        // the retried callback is ALSO stateless we surface the error rather
        // than loop.
        const isStaleState = /no matching state/i.test(message);
        if (isStaleState && !sessionStorage.getItem(STALE_STATE_RETRY_KEY)) {
          sessionStorage.setItem(STALE_STATE_RETRY_KEY, '1');
          await userManager.signinRedirect({ redirect_uri: callbackUri });
          return;
        }
        sessionStorage.removeItem(STALE_STATE_RETRY_KEY);
        throw {
          redirectTo: false,
          message: isStaleState ? 'auth.session_expired' : message,
        };
      }
    },
  };
};

/**
 * Helper function to get the current access token
 * Useful for making authenticated API calls outside of the data provider
 */
export const getAccessToken = async (userManager: UserManager): Promise<string | null> => {
  const user = await userManager.getUser();
  return user?.access_token || null;
};
